import {
  bindWalletEvents,
  contractWithSigner,
  connectWallet,
  ensurePulseChainConnected,
  rehydrateWalletSession,
  walletState,
} from '../shared/wallet';
import { DEPLOYMENT, getActiveChainConfig, getActiveNetworkKey, setActiveNetwork, type NetworkKey } from '../shared/config';
import { erc20Abi, erc20AbiEthers, factoryAbi, factoryAbiEthers, lookerAbi, marketAbi } from '../shared/abi';
import { fmtDuration, fmtPct, fmtToken, shortAddr } from '../shared/format';
import { readClient, readContractsBatch } from '../shared/client';
import { resolveTokenLogoUrlSync } from '../shared/collateral';
import { parseUnits } from 'ethers';
import { openAccountModal } from '../shared/accountModal';
import { getMarketPriorityMap, getMarketPrioritySortKey } from '../shared/marketPriority';
import { escapeHtml } from '../shared/sanitize';
import type { Address } from 'viem';

const MAX_OUTCOME_TOKEN_LABEL_BYTES = 25;
const MARKET_PAGE_SIZE = 6;
const MARKET_PAGE_SIZE_REDUCED = 6;
const AUTO_REFRESH_NEW_HEAD_LIMIT = 6;
const AUTO_REFRESH_RETENTION_LIMIT = 50;
const MAX_MARKETS_VISIBLE = 50;
const MARKET_STATIC_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MARKET_LIST_CACHE_TTL_MS = 5 * 60 * 1000;
const MARKET_LIST_CACHE_VERSION = 1;
const LIST_REFRESH_FOCUSED_MS = 20_000;
const LIST_REFRESH_VISIBLE_MS = 45_000;
const LIST_REFRESH_HIDDEN_MS = 120_000;
const REFRESH_ICON_SVG = `<img src="${import.meta.env.BASE_URL}images/refreshing.svg" alt="" aria-hidden="true"/>`;

type EnrichedMarketRow = {
  address: `0x${string}`;
  question: string;
  resolveAfter: number;
  resolved: boolean;
  tradingPaused: boolean;
  bucket?: number;
  yesReserve: bigint;
  noReserve: bigint;
  marketValue: bigint;
  total: bigint;
  left: number;
  yesLabel: string;
  noLabel: string;
  collateralAddr: `0x${string}`;
  collateralSymbol: string;
  collateralDecimals: number;
  collateralLogoUrl: string;
  collateralBalance: bigint;
  claimable: boolean;
  claimPayout: bigint;
  listed: boolean;
  promoted: number;
  priorityKey: number;
};

type ManualFrontPageMarket = {
  address: `0x${string}`;
  title: string;
  note?: string;
};

type CachedEnrichedMarketRow = {
  address: `0x${string}`;
  question: string;
  resolveAfter: number;
  resolved: boolean;
  tradingPaused: boolean;
  bucket?: number;
  yesReserve: string;
  noReserve: string;
  marketValue: string;
  total: string;
  yesLabel: string;
  noLabel: string;
  collateralAddr: `0x${string}`;
  collateralSymbol: string;
  collateralDecimals: number;
  collateralLogoUrl: string;
  collateralBalance: string;
  listed: boolean;
  promoted: number;
  priorityKey: number;
};

type MarketListCachePayload = {
  version: number;
  cachedAtMs: number;
  totalMarketCount: number;
  nextIndexCursor: number;
  rows: CachedEnrichedMarketRow[];
};

// Placeholder for manually pinned markets on the homepage.
// Add entries here when you want selected markets to appear above the Markets panel.
const MANUAL_FRONT_PAGE_MARKETS: ManualFrontPageMarket[] = [
  // {
  //   address: '0x0000000000000000000000000000000000000000',
  //   title: 'Market title',
  //   note: 'note',
  // },
];

export function mountIndexView(app: HTMLElement): void {
  const activeChain = getActiveChainConfig();
  const activeNetwork = getActiveNetworkKey();
  const explorerBaseUrl = (activeChain.blockExplorerUrls?.[0] || '').replace(/\/+$/, '');
  const heroLinkHref = activeNetwork === 'testnet' ? 'https://faucet.v4.testnet.pulsechain.com/' : 'https://pulsechain.com/';
  const heroLinkLabel = activeNetwork === 'testnet' ? 'Get testnet PLS from faucet.v4.testnet.pulsechain.com' : 'Visit PulseChain.com';
  app.innerHTML = `
  <div class="app">
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark" aria-hidden="true"></div>
        <div>
          <div class="brand-text">BoxBet</div>
          <div class="brand-sub">${activeChain.chainName} · ${activeChain.id}</div>
        </div>
      </div>
      <div class="header-actions">
        <details class="header-dropdown" id="networkDropdown">
          <summary class="header-dropdown-btn" aria-label="Network selector">
            <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="9" fill="none" stroke-width="2"/>
              <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" fill="none" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="header-dropdown-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="header-dropdown-menu" role="menu" aria-label="Network selector">
            <button id="networkMainnetBtn" class="header-dropdown-item ${activeNetwork === 'mainnet' ? 'active' : ''}" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2v20M2 12h20" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                Mainnet
              </span>
            </button>
            <button id="networkTestnetBtn" class="header-dropdown-item ${activeNetwork === 'testnet' ? 'active' : ''}" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 6h16v12H4zM8 10h8" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Testnet
              </span>
            </button>
          </div>
        </details>
        <details class="header-dropdown" id="linksDropdown">
          <summary class="header-dropdown-btn" aria-label="Header links">
            <svg class="header-dropdown-trigger-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" fill="none" stroke-width="2" stroke-linejoin="round"/>
              <path d="M4 7.5l8 4.5 8-4.5M12 12v9" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="header-dropdown-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="header-dropdown-menu" role="menu" aria-label="Navigation links">
            <a href="/boxbet/help" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 18h.01M9.1 9a3 3 0 1 1 5.8 1c-.57.78-1.55 1.23-2.18 2" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Help
              </span>
            </a>
            <button id="headerCreateMarketBtn" class="header-dropdown-item" type="button" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                Create Market
              </span>
            </button>
            <a href="/boxbet/adversary" class="header-dropdown-item" role="menuitem">
              <span class="header-dropdown-item-label">
                <svg class="header-dropdown-item-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l8 4v5c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V7l8-4z" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Adversary
              </span>
            </a>
          </div>
        </details>
        <button id="connectBtn" class="wallet-pill">Connect Wallet</button>
      </div>
    </header>

    <section class="hero" id="heroSection">
      <div class="hero-pill">
        <span class="hero-pill-icon" aria-hidden="true">!</span>
        <span class="hero-pill-text">An onchain forecasting market where users price future outcomes, challenge resolutions, and claim payouts on PulseChain.</span>
        <a class="hero-pill-link" href="${heroLinkHref}" target="_blank" rel="noopener noreferrer">${heroLinkLabel}</a>
        <button id="heroCloseBtn" class="hero-pill-close" type="button" aria-label="Hide message" title="Hide message">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6 6l12 12M18 6L6 18" fill="none" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </section>

    <section id="manualMarketsSection" style="display:none; margin-top:8px; padding:0 14px 12px;">
      <div class="section-head" style="padding:0 0 10px;">
        <div class="section-title">Featured Markets</div>
      </div>
      <div id="manualMarketsList" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 290px), 1fr)); gap:12px;"></div>
    </section>

    <section style="margin-top:16px;">
      <div style="padding:14px;" class="section-head">
        <div class="section-title">Markets</div>
      </div>
      <div class="tabs" style="padding:0 14px 14px;">
        <button type="button" class="tab active" data-tab="liveMarkets" id="liveTab">Live <span id="liveCount" class="mono" style="font-size:.65rem; opacity:.8;">(0)</span></button>
        <button type="button" class="tab" data-tab="finalMarkets" id="finalTab">Final <span id="finalCount" class="mono" style="font-size:.65rem; opacity:.8;">(0)</span></button>
        <button id="marketSearchBtn" class="btn icon-btn market-search-open-btn" aria-label="Search market by address" title="Search market by address">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="2"/>
            <path d="M16 16l5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <button type="button" class="tab" data-tab="claimsMarkets" id="claimsTab" style="display:none;">Claims <span id="claimsCount" class="mono" style="font-size:.65rem; opacity:.8;">(0)</span></button>
        <button id="refreshBtn" class="btn icon-btn" style="margin-left:10px;" aria-label="Refresh markets" title="Refresh">${REFRESH_ICON_SVG}</button>
      </div>
      <div id="livePanel" class="panel active" style="padding:0 14px 14px;">
        <div id="liveMarkets" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 310px), 1fr)); gap:16px;"></div>
      </div>
      <div id="finalPanel" class="panel" style="padding:0 14px 14px;">
        <div id="finalMarkets" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 310px), 1fr)); gap:16px;"></div>
      </div>
      <div id="claimsPanel" class="panel" style="padding:0 14px 14px;">
        <div id="claimsHint" class="mono" style="font-size:.7rem; color:var(--muted); margin-bottom:10px;"></div>
        <div id="claimsMarkets" style="display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 310px), 1fr)); gap:16px;"></div>
      </div>
      <div style="padding:0 14px 14px; display:flex; justify-content:center;">
        <button id="loadMoreBtn" class="btn" style="display:none;">Load More Markets</button>
      </div>
      <div id="marketsLavaLoader" class="markets-lava-loader" aria-hidden="true" hidden></div>
    </section>

    <div id="createModal" style="display:none; position:fixed; inset:0; background:rgba(0,0,0,.72); align-items:center; justify-content:center; z-index:100;">
      <div class="card" style="max-width:540px; width:100%; padding:16px;">
        <div style="font-weight:700; letter-spacing:.08em; color:var(--cyan); margin-bottom:12px;">CREATE MARKET</div>
        <div style="display:grid; gap:10px;">
          <div class="card" style="padding:10px;">
            <div class="mono" style="font-size:.66rem; color:var(--muted); margin-bottom:8px;">CREATE MARKET FUNDING</div>
            <div style="display:grid; gap:6px; font-size:.82rem;">
              <div style="display:flex; justify-content:space-between; gap:8px;"><span class="mono" style="color:var(--muted)">Wallet USDC</span><span id="walletUsdcCreate">-</span></div>
              <div style="display:flex; justify-content:space-between; gap:8px;"><span class="mono" style="color:var(--muted)">Required Collateral</span><span id="requiredCollateralCreate">-</span></div>
              <div style="display:flex; justify-content:space-between; gap:8px;"><span class="mono" style="color:var(--muted)">Factory Allowance</span><span id="factoryAllowanceCreate">-</span></div>
            </div>
            <div id="createFundingStatus" class="mono" style="font-size:.66rem; color:var(--muted); margin-top:8px;"></div>
          </div>
          <label class="mono" style="font-size:.72rem; color:var(--muted)">Question</label>
          <input id="q" class="btn" style="text-align:left; background:#061320" />
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
            <div>
              <label class="mono" style="font-size:.72rem; color:var(--muted)">Outcome 1 Token Name</label>
              <input id="yesName" class="btn" style="text-align:left; background:#061320" value="BoxBet 1" maxlength="64" />
            </div>
            <div>
              <label class="mono" style="font-size:.72rem; color:var(--muted)">Outcome 1 Token Symbol</label>
              <input id="yesSymbol" class="btn" style="text-align:left; background:#061320" value="b1" maxlength="64" />
            </div>
          </div>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
            <div>
              <label class="mono" style="font-size:.72rem; color:var(--muted)">Outcome 2 Token Name</label>
              <input id="noName" class="btn" style="text-align:left; background:#061320" value="BoxBet 2" maxlength="64" />
            </div>
            <div>
              <label class="mono" style="font-size:.72rem; color:var(--muted)">Outcome 2 Token Symbol</label>
              <input id="noSymbol" class="btn" style="text-align:left; background:#061320" value="b2" maxlength="64" />
            </div>
          </div>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
            <div>
              <label class="mono" style="font-size:.72rem; color:var(--muted)">Resolve In Hours</label>
              <input id="h" type="number" class="btn" style="text-align:left; background:#061320" value="24" />
            </div>
            <div>
              <label class="mono" style="font-size:.72rem; color:var(--muted)">Initial Liquidity (USDC)</label>
              <input id="liq" type="number" class="btn" style="text-align:left; background:#061320" value="100000" min="1" step="1" />
            </div>
          </div>
          <div class="mono" style="font-size:.68rem; color:var(--muted)">
            Factory will transfer <span id="requiredCollateral">200000</span> USDC collateral (2x initial liquidity) from your wallet.
          </div>
          <div style="display:flex; gap:8px; justify-content:flex-end;">
            <button id="cancelCreate" class="btn">Cancel</button>
            <button id="confirmCreate" class="btn">Submit</button>
          </div>
        </div>
      </div>
    </div>

    <div id="marketSearchModal" class="market-search-modal" hidden>
      <div class="market-search-sheet card" role="dialog" aria-modal="true" aria-labelledby="marketSearchTitle">
        <div class="market-search-head">
          <div id="marketSearchTitle" class="market-search-title">Find Market By Address</div>
          <button id="marketSearchCloseBtn" type="button" class="btn icon-btn market-search-close" aria-label="Close search">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6l-12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="market-search-subtitle">Paste a market contract address to jump directly to it.</div>
        <div class="market-search-input-row">
          <input id="marketSearchInput" class="market-search-input" placeholder="0x..." autocomplete="off" spellcheck="false" />
          <button id="marketSearchSubmitBtn" type="button" class="btn btn-primary market-search-submit">Search</button>
        </div>
        <div id="marketSearchStatus" class="mono market-search-status">Enter an address to begin.</div>
        <div id="marketSearchResult" class="market-search-result"></div>
      </div>
    </div>
  </div>
`;

  const connectBtn = app.querySelector('#connectBtn') as HTMLButtonElement;
  const networkMainnetBtn = app.querySelector('#networkMainnetBtn') as HTMLButtonElement;
  const networkTestnetBtn = app.querySelector('#networkTestnetBtn') as HTMLButtonElement;
  const networkDropdown = app.querySelector('#networkDropdown') as HTMLDetailsElement;
  const linksDropdown = app.querySelector('#linksDropdown') as HTMLDetailsElement;
  const heroSectionEl = app.querySelector('#heroSection') as HTMLElement;
  const heroCloseBtn = app.querySelector('#heroCloseBtn') as HTMLButtonElement;
  const refreshBtn = app.querySelector('#refreshBtn') as HTMLButtonElement;
  const loadMoreBtn = app.querySelector('#loadMoreBtn') as HTMLButtonElement;
  const headerCreateMarketBtn = app.querySelector('#headerCreateMarketBtn') as HTMLButtonElement;
  const livePanelEl = app.querySelector('#livePanel') as HTMLElement;
  const finalPanelEl = app.querySelector('#finalPanel') as HTMLElement;
  const claimsPanelEl = app.querySelector('#claimsPanel') as HTMLElement;
  const liveMarketsEl = app.querySelector('#liveMarkets') as HTMLElement;
  const finalMarketsEl = app.querySelector('#finalMarkets') as HTMLElement;
  const claimsMarketsEl = app.querySelector('#claimsMarkets') as HTMLElement;
  const claimsHintEl = app.querySelector('#claimsHint') as HTMLElement;
  const claimsTabBtn = app.querySelector('#claimsTab') as HTMLButtonElement;
  const liveCountEl = app.querySelector('#liveCount') as HTMLElement;
  const finalCountEl = app.querySelector('#finalCount') as HTMLElement;
  const claimsCountEl = app.querySelector('#claimsCount') as HTMLElement;
  const marketSearchBtn = app.querySelector('#marketSearchBtn') as HTMLButtonElement;
  const marketSearchModal = app.querySelector('#marketSearchModal') as HTMLElement;
  const marketSearchCloseBtn = app.querySelector('#marketSearchCloseBtn') as HTMLButtonElement;
  const marketSearchInput = app.querySelector('#marketSearchInput') as HTMLInputElement;
  const marketSearchSubmitBtn = app.querySelector('#marketSearchSubmitBtn') as HTMLButtonElement;
  const marketSearchStatus = app.querySelector('#marketSearchStatus') as HTMLElement;
  const marketSearchResult = app.querySelector('#marketSearchResult') as HTMLElement;
  const manualMarketsSectionEl = app.querySelector('#manualMarketsSection') as HTMLElement;
  const manualMarketsListEl = app.querySelector('#manualMarketsList') as HTMLElement;
  const marketsLavaLoader = app.querySelector('#marketsLavaLoader') as HTMLElement;
  const modal = app.querySelector('#createModal') as HTMLElement;
  const liquidityInput = app.querySelector('#liq') as HTMLInputElement;
  const requiredCollateralEl = app.querySelector('#requiredCollateral') as HTMLElement;
  const walletUsdcCreateEl = app.querySelector('#walletUsdcCreate') as HTMLElement;
  const requiredCollateralCreateEl = app.querySelector('#requiredCollateralCreate') as HTMLElement;
  const factoryAllowanceCreateEl = app.querySelector('#factoryAllowanceCreate') as HTMLElement;
  const createFundingStatusEl = app.querySelector('#createFundingStatus') as HTMLElement;

  heroCloseBtn.addEventListener('click', () => {
    heroSectionEl.style.display = 'none';
  });

  const toastHost = document.createElement('div');
  toastHost.className = 'bb-toast-host';
  toastHost.innerHTML = `
    <div id="bbToast" class="bb-toast" role="status" aria-live="polite" hidden>
      <span id="bbToastIcon" class="bb-toast-icon" aria-hidden="true">i</span>
      <span id="bbToastText" class="bb-toast-text"></span>
      <a id="bbToastLink" class="bb-toast-link" href="#" target="_blank" rel="noopener noreferrer" hidden>View tx</a>
    </div>
  `;
  app.appendChild(toastHost);

  const bbToastEl = app.querySelector('#bbToast') as HTMLElement;
  const bbToastIconEl = app.querySelector('#bbToastIcon') as HTMLElement;
  const bbToastTextEl = app.querySelector('#bbToastText') as HTMLElement;
  const bbToastLinkEl = app.querySelector('#bbToastLink') as HTMLAnchorElement;
  let bbToastTimer: number | null = null;
  let bbToastHideTimer: number | null = null;

  function buildTxLink(txHash: string): string | null {
    if (!explorerBaseUrl) return null;
    return `${explorerBaseUrl}/tx/${txHash}`;
  }

  function showToast(
    kind: 'success' | 'error' | 'warning' | 'info',
    message: string,
    options?: { txHash?: string; href?: string; linkLabel?: string; durationMs?: number },
  ) {
    if (bbToastTimer !== null) {
      window.clearTimeout(bbToastTimer);
      bbToastTimer = null;
    }
    if (bbToastHideTimer !== null) {
      window.clearTimeout(bbToastHideTimer);
      bbToastHideTimer = null;
    }

    const icon = kind === 'success'
      ? '+'
      : kind === 'error'
        ? 'x'
        : kind === 'warning'
          ? '!'
          : 'i';

    bbToastIconEl.textContent = icon;
    bbToastTextEl.textContent = message;
    const href = options?.href || (options?.txHash ? buildTxLink(options.txHash) : null);
    if (href) {
      bbToastLinkEl.href = href;
      bbToastLinkEl.textContent = options?.linkLabel || 'View tx';
      bbToastLinkEl.hidden = false;
    } else {
      bbToastLinkEl.hidden = true;
      bbToastLinkEl.removeAttribute('href');
    }

    bbToastEl.hidden = false;
    bbToastEl.classList.remove('is-success', 'is-error', 'is-warning', 'is-info', 'is-visible');
    void bbToastEl.offsetWidth;
    bbToastEl.classList.add(`is-${kind}`, 'is-visible');

    const timeoutMs = options?.durationMs ?? (href ? 6500 : 2200);

    bbToastTimer = window.setTimeout(() => {
      bbToastEl.classList.remove('is-visible');
      bbToastHideTimer = window.setTimeout(() => {
        bbToastEl.hidden = true;
      }, 180);
    }, timeoutMs);
  }

  function renderManualFrontPageMarkets() {
    if (MANUAL_FRONT_PAGE_MARKETS.length === 0) {
      manualMarketsSectionEl.style.display = 'none';
      manualMarketsListEl.innerHTML = '';
      return;
    }

    manualMarketsSectionEl.style.display = '';
    manualMarketsListEl.innerHTML = MANUAL_FRONT_PAGE_MARKETS.map((market) => `
      <a href="?market=${market.address}" class="market-card" style="min-height:auto; gap:8px;">
        <div class="mono tiny">${shortAddr(market.address)}</div>
        <div class="q" style="font-size:.9rem;">${escapeHtml(market.title)}</div>
        ${market.note ? `<div class="mono tiny" style="color:var(--muted)">${escapeHtml(market.note)}</div>` : ''}
      </a>
    `).join('');
  }

  renderManualFrontPageMarkets();

  const marketPriorityMap = getMarketPriorityMap(activeNetwork);

  let totalMarketCount = 0;
  let nextIndexCursor = -1;
  let loadedRows: EnrichedMarketRow[] = [];
  let pageLoading = false;
  let lavaLoaderInjected = false;

  function setMarketsLoading(active: boolean): void {
    pageLoading = active;
    if (active) {
      if (!lavaLoaderInjected) {
        lavaLoaderInjected = true;
        marketsLavaLoader.innerHTML = `<svg class="tuc-lava-lamp" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMax meet" style="background:none;" aria-hidden="true"><defs><filter id="lava-filter-idx" filterUnits="userSpaceOnUse" x="0" y="0" width="100" height="100"><feGaussianBlur in="SourceGraphic" stdDeviation="3"/><feComponentTransfer result="cutoff"><feFuncA type="linear" slope="10" intercept="-5"/></feComponentTransfer></filter></defs><g filter="url(#lava-filter-idx)"><circle cx="46" cy="143.359" r="34.9216" fill="#ff2244"><animate attributeName="cy" values="151.789;-27.745" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.964s"/><animate attributeName="r" values="38;0;0" keyTimes="0;0.58;1" dur="1s" repeatCount="indefinite" begin="-0.964s"/></circle><circle cx="42" cy="46.44" r="12.8028" fill="#ff2244"><animate attributeName="cy" values="131.056;-25.346" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.458s"/><animate attributeName="r" values="29;0;0" keyTimes="0;0.969;1" dur="1s" repeatCount="indefinite" begin="-0.458s"/></circle><circle cx="70" cy="123.801" r="16.475" fill="#ff2244"><animate attributeName="cy" values="131.021;-1.974" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.971s"/><animate attributeName="r" values="18;0;0" keyTimes="0;0.641;1" dur="1s" repeatCount="indefinite" begin="-0.971s"/></circle><circle cx="48" cy="115.146" r="30.676" fill="#ff2244"><animate attributeName="cy" values="152.091;-31.887" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.117s"/><animate attributeName="r" values="47;0;0" keyTimes="0;0.578;1" dur="1s" repeatCount="indefinite" begin="-0.117s"/></circle><circle cx="51" cy="103.348" r="35.269" fill="#ff2244"><animate attributeName="cy" values="149.603;-44.000" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.156s"/><animate attributeName="r" values="47;0;0" keyTimes="0;0.957;1" dur="1s" repeatCount="indefinite" begin="-0.156s"/></circle></g><g filter="url(#lava-filter-idx)"><circle cx="48" cy="57.484" r="5.379" fill="#2ec989"><animate attributeName="cy" values="121.802;-9.149" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.408s"/><animate attributeName="r" values="17;0;0" keyTimes="0;0.719;1" dur="1s" repeatCount="indefinite" begin="-0.408s"/></circle><circle cx="74" cy="57.772" r="5.98" fill="#2ec989"><animate attributeName="cy" values="125.679;-22.735" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.374s"/><animate attributeName="r" values="24;0;0" keyTimes="0;0.609;1" dur="1s" repeatCount="indefinite" begin="-0.374s"/></circle><circle cx="62" cy="108.733" r="12.974" fill="#2ec989"><animate attributeName="cy" values="140.296;-4.080" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.135s"/><animate attributeName="r" values="22;0;0" keyTimes="0;0.533;1" dur="1s" repeatCount="indefinite" begin="-0.135s"/></circle><circle cx="67" cy="112.025" r="20.853" fill="#2ec989"><animate attributeName="cy" values="131.981;-15.021" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.052s"/><animate attributeName="r" values="27;0;0" keyTimes="0;0.596;1" dur="1s" repeatCount="indefinite" begin="-0.052s"/></circle><circle cx="49" cy="121.86" r="24.606" fill="#2ec989"><animate attributeName="cy" values="127.051;-9.829" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.955s"/><animate attributeName="r" values="26;0;0" keyTimes="0;0.707;1" dur="1s" repeatCount="indefinite" begin="-0.955s"/></circle></g><g filter="url(#lava-filter-idx)"><circle cx="48" cy="50.653" r="3.584" fill="#ff7700"><animate attributeName="cy" values="133.793;-12.191" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.486s"/><animate attributeName="r" values="17;0;0" keyTimes="0;0.722;1" dur="1s" repeatCount="indefinite" begin="-0.486s"/></circle><circle cx="61" cy="69.648" r="5.629" fill="#ff7700"><animate attributeName="cy" values="122.801;3.820" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.363s"/><animate attributeName="r" values="12;0;0" keyTimes="0;0.841;1" dur="1s" repeatCount="indefinite" begin="-0.363s"/></circle><circle cx="27" cy="107.213" r="12.508" fill="#ff7700"><animate attributeName="cy" values="133.345;-3.184" keyTimes="0;1" dur="1s" repeatCount="indefinite" begin="-0.108s"/><animate attributeName="r" values="17;0;0" keyTimes="0;0.724;1" dur="1s" repeatCount="indefinite" begin="-0.108s"/></circle></g></svg>`;
      }
      marketsLavaLoader.hidden = false;
      marketsLavaLoader.classList.add('is-active');
    } else {
      marketsLavaLoader.hidden = true;
      marketsLavaLoader.classList.remove('is-active');
    }
  }
  let loadingGeneration = 0;
  let dynamicPageSize = MARKET_PAGE_SIZE;
  let listRefreshTimer: number | null = null;
  let delayedListRefreshTimer: number | null = null;
  let loadMarketsInFlight: Promise<void> | null = null;
  let activeTabKey: 'liveMarkets' | 'finalMarkets' | 'claimsMarkets' = 'liveMarkets';
  let claimsDataReady = false;
  const marketListCacheStorageKey = `boxbet:index:market-list:${activeNetwork}`;

  function dedupeRowsByAddress(rows: EnrichedMarketRow[]): EnrichedMarketRow[] {
    const byAddress = new Map<string, EnrichedMarketRow>();
    for (const row of rows) {
      const key = String(row.address || '').toLowerCase();
      if (!key || byAddress.has(key)) continue;
      byAddress.set(key, row);
    }
    return Array.from(byAddress.values()).slice(0, MAX_MARKETS_VISIBLE);
  }

  function parseBigIntCached(value: unknown, fallback: bigint = 0n): bigint {
    try {
      if (typeof value === 'bigint') return value;
      if (typeof value === 'number' && Number.isFinite(value)) return BigInt(Math.trunc(value));
      if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return BigInt(value.trim());
      return fallback;
    } catch {
      return fallback;
    }
  }

  function toCachedRow(row: EnrichedMarketRow): CachedEnrichedMarketRow {
    return {
      address: row.address,
      question: row.question,
      resolveAfter: row.resolveAfter,
      resolved: row.resolved,
      tradingPaused: row.tradingPaused,
      bucket: row.bucket,
      yesReserve: row.yesReserve.toString(),
      noReserve: row.noReserve.toString(),
      marketValue: row.marketValue.toString(),
      total: row.total.toString(),
      yesLabel: row.yesLabel,
      noLabel: row.noLabel,
      collateralAddr: row.collateralAddr,
      collateralSymbol: row.collateralSymbol,
      collateralDecimals: row.collateralDecimals,
      collateralLogoUrl: row.collateralLogoUrl,
      collateralBalance: row.collateralBalance.toString(),
      listed: row.listed,
      promoted: row.promoted,
      priorityKey: row.priorityKey,
    };
  }

  function persistMarketListCache(): void {
    try {
      const uniqueRows = dedupeRowsByAddress(loadedRows);
      const payload: MarketListCachePayload = {
        version: MARKET_LIST_CACHE_VERSION,
        cachedAtMs: Date.now(),
        totalMarketCount,
        nextIndexCursor,
        rows: uniqueRows.map(toCachedRow),
      };
      window.localStorage.setItem(marketListCacheStorageKey, JSON.stringify(payload));
    } catch {
      // ignore cache persistence failures
    }
  }

  function restoreMarketListCache(): void {
    try {
      const raw = window.localStorage.getItem(marketListCacheStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as MarketListCachePayload;
      if (!parsed || parsed.version !== MARKET_LIST_CACHE_VERSION) return;
      if (Date.now() - Number(parsed.cachedAtMs || 0) > MARKET_LIST_CACHE_TTL_MS) return;
      if (!Array.isArray(parsed.rows) || parsed.rows.length === 0) return;

      const nowTs = Math.floor(Date.now() / 1000);
      loadedRows = parsed.rows.slice(0, MAX_MARKETS_VISIBLE).map((row) => {
        const resolveAfter = Number(row.resolveAfter || 0);
        const collateralDecimalsRaw = Number(row.collateralDecimals ?? 18);
        const collateralDecimals = Number.isFinite(collateralDecimalsRaw) && collateralDecimalsRaw >= 0 && collateralDecimalsRaw <= 36
          ? collateralDecimalsRaw
          : 18;
        return {
          address: row.address,
          question: String(row.question || 'Unknown market'),
          resolveAfter,
          resolved: Boolean(row.resolved),
          tradingPaused: Boolean(row.tradingPaused),
          bucket: typeof row.bucket === 'number' ? row.bucket : undefined,
          yesReserve: parseBigIntCached(row.yesReserve),
          noReserve: parseBigIntCached(row.noReserve),
          marketValue: parseBigIntCached(row.marketValue),
          total: parseBigIntCached(row.total),
          left: resolveAfter - nowTs,
          yesLabel: String(row.yesLabel || '-'),
          noLabel: String(row.noLabel || '-'),
          collateralAddr: row.collateralAddr || (DEPLOYMENT.collateral as `0x${string}`),
          collateralSymbol: String(row.collateralSymbol || 'TOKEN'),
          collateralDecimals,
          collateralLogoUrl: String(row.collateralLogoUrl || `${import.meta.env.BASE_URL}images/coin.svg`),
          collateralBalance: parseBigIntCached(row.collateralBalance),
          // claims are account-scoped and should be freshly loaded on demand
          claimable: false,
          claimPayout: 0n,
          listed: Boolean(row.listed),
          promoted: Number(row.promoted || 0),
          priorityKey: Number(row.priorityKey || 0),
        };
      });
      loadedRows = dedupeRowsByAddress(loadedRows);

      const cachedCountRaw = Number(parsed.totalMarketCount || loadedRows.length);
      totalMarketCount = Number.isFinite(cachedCountRaw)
        ? Math.max(loadedRows.length, cachedCountRaw)
        : loadedRows.length;

      const cursorRaw = Number(parsed.nextIndexCursor);
      const fallbackCursor = Math.max(-1, totalMarketCount - loadedRows.length - 1);
      nextIndexCursor = Number.isFinite(cursorRaw) ? Math.max(-1, cursorRaw) : fallbackCursor;

      claimsDataReady = false;
      renderMarketSections();
    } catch {
      // ignore cache restore failures
    }
  }

  type MarketStaticDetail = {
    address: `0x${string}`;
    question: string;
    collateralAddr: `0x${string}`;
    yesTokenAddr: `0x${string}`;
    noTokenAddr: `0x${string}`;
    yesLabel: string;
    noLabel: string;
    cachedAtMs: number;
  };

  const staticCacheStorageKey = `boxbet:index:market-static:${activeNetwork}`;
  const marketStaticCache = new Map<string, MarketStaticDetail>();

  function restoreStaticCache(): void {
    try {
      const raw = window.localStorage.getItem(staticCacheStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as MarketStaticDetail[];
      const now = Date.now();
      for (const item of parsed || []) {
        if (!item?.address) continue;
        if (now - Number(item.cachedAtMs || 0) > MARKET_STATIC_CACHE_TTL_MS) continue;
        marketStaticCache.set(String(item.address).toLowerCase(), item);
      }
    } catch {
      // ignore cache restore failures
    }
  }

  function persistStaticCache(): void {
    try {
      const now = Date.now();
      const rows = Array.from(marketStaticCache.values())
        .filter((item) => now - Number(item.cachedAtMs || 0) <= MARKET_STATIC_CACHE_TTL_MS)
        .slice(-400);
      window.localStorage.setItem(staticCacheStorageKey, JSON.stringify(rows));
    } catch {
      // ignore cache persistence failures
    }
  }

  async function resolveMarketStaticDetails(addresses: `0x${string}`[]): Promise<Map<string, MarketStaticDetail>> {
    const now = Date.now();
    const result = new Map<string, MarketStaticDetail>();
    const staleOrMissing: `0x${string}`[] = [];

    for (const address of addresses) {
      const key = address.toLowerCase();
      const cached = marketStaticCache.get(key);
      const hasValidCollateral = Boolean(cached?.collateralAddr && /^0x[0-9a-fA-F]{40}$/.test(String(cached.collateralAddr)));
      if (cached && hasValidCollateral && now - cached.cachedAtMs <= MARKET_STATIC_CACHE_TTL_MS) {
        result.set(key, cached);
      } else {
        staleOrMissing.push(address);
      }
    }

    if (staleOrMissing.length > 0) {
      const detailCalls = staleOrMissing.flatMap((address) => [
        { address, abi: marketAbi, functionName: 'question' },
        { address, abi: marketAbi, functionName: 'collateral' },
        { address, abi: marketAbi, functionName: 'yesToken' },
        { address, abi: marketAbi, functionName: 'noToken' },
      ]);
      const detail = await readContractsBatch<unknown>(detailCalls);
      const tokenAddresses: `0x${string}`[] = [];
      for (let idx = 0; idx < staleOrMissing.length; idx += 1) {
        const offset = idx * 4;
        const yesTokenAddr = (detail[offset + 2] as `0x${string}`) || ('0x0000000000000000000000000000000000000000' as `0x${string}`);
        const noTokenAddr = (detail[offset + 3] as `0x${string}`) || ('0x0000000000000000000000000000000000000000' as `0x${string}`);
        tokenAddresses.push(yesTokenAddr, noTokenAddr);
      }

      const nameCalls = tokenAddresses.map((address) => ({ address, abi: erc20Abi, functionName: 'name' }));
      const names = await readContractsBatch<unknown>(nameCalls);

      for (let idx = 0; idx < staleOrMissing.length; idx += 1) {
        const address = staleOrMissing[idx];
        const offset = idx * 4;
        const tokenOffset = idx * 2;
        const collateralAddr = (detail[offset + 1] as `0x${string}`) || (DEPLOYMENT.collateral as `0x${string}`);
        const row: MarketStaticDetail = {
          address,
          question: String(detail[offset] ?? 'Unknown market'),
          collateralAddr,
          yesTokenAddr: (detail[offset + 2] as `0x${string}`) || ('0x0000000000000000000000000000000000000000' as `0x${string}`),
          noTokenAddr: (detail[offset + 3] as `0x${string}`) || ('0x0000000000000000000000000000000000000000' as `0x${string}`),
          yesLabel: String(names[tokenOffset] ?? '-'),
          noLabel: String(names[tokenOffset + 1] ?? '-'),
          cachedAtMs: now,
        };
        const key = row.address.toLowerCase();
        marketStaticCache.set(key, row);
        result.set(key, row);
      }
      persistStaticCache();
    }

    return result;
  }

  restoreStaticCache();

  async function getMarketCount(): Promise<number> {
    const countRaw = await readClient.readContract({
      address: DEPLOYMENT.factory as `0x${string}`,
      abi: factoryAbi,
      functionName: 'marketCount',
    });
    return Number(countRaw as bigint);
  }

  function getListRefreshIntervalMs(): number {
    if (document.hidden) return LIST_REFRESH_HIDDEN_MS;
    if (document.hasFocus()) return LIST_REFRESH_FOCUSED_MS;
    return LIST_REFRESH_VISIBLE_MS;
  }

  function clearListRefreshTimers(): void {
    if (listRefreshTimer !== null) {
      window.clearTimeout(listRefreshTimer);
      listRefreshTimer = null;
    }
    if (delayedListRefreshTimer !== null) {
      window.clearTimeout(delayedListRefreshTimer);
      delayedListRefreshTimer = null;
    }
  }

  function scheduleListAutoRefresh(nextDelayMs?: number): void {
    if (listRefreshTimer !== null) {
      window.clearTimeout(listRefreshTimer);
      listRefreshTimer = null;
    }

    const delayMs = typeof nextDelayMs === 'number' ? Math.max(1_000, nextDelayMs) : getListRefreshIntervalMs();
    listRefreshTimer = window.setTimeout(() => {
      void refreshMarketListAuto()
        .catch((error: any) => {
          showToast('warning', error?.message || 'Using last known market data due to RPC failure');
        })
        .finally(() => {
          scheduleListAutoRefresh();
        });
    }, delayMs);
  }

  function schedulePostMutationListRefreshes(): void {
    if (delayedListRefreshTimer !== null) {
      window.clearTimeout(delayedListRefreshTimer);
      delayedListRefreshTimer = null;
    }

    void refreshMarketListAuto();
    delayedListRefreshTimer = window.setTimeout(() => {
      void refreshMarketListAuto();
    }, 6_000);
  }

  function shouldLoadClaimsData(): boolean {
    return Boolean(walletState.account) && activeTabKey === 'claimsMarkets';
  }

  function onPageVisibilityOrFocusChanged(): void {
    scheduleListAutoRefresh();
  }

  function onNetworkToggle(next: NetworkKey) {
    if (next === getActiveNetworkKey()) return;
    setActiveNetwork(next);
    window.location.reload();
  }

  networkMainnetBtn.addEventListener('click', () => onNetworkToggle('mainnet'));
  networkTestnetBtn.addEventListener('click', () => onNetworkToggle('testnet'));

  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    if (!networkDropdown.contains(target)) networkDropdown.open = false;
    if (!linksDropdown.contains(target)) linksDropdown.open = false;
  });

  const tabs = Array.from(app.querySelectorAll('.tab[data-tab]')) as HTMLButtonElement[];
  const panelByTab: Record<string, HTMLElement> = {
    liveMarkets: livePanelEl,
    finalMarkets: finalPanelEl,
    claimsMarkets: claimsPanelEl,
  };

  function activateTab(panelKey: string | null) {
    if (!panelKey) return;
    const tab = tabs.find((t) => t.dataset.tab === panelKey);
    const panel = panelByTab[panelKey];
    if (!tab || !panel) return;
    tabs.forEach((t) => t.classList.remove('active'));
    Object.values(panelByTab).forEach((p) => p.classList.remove('active'));
    tab.classList.add('active');
    panel.classList.add('active');
    activeTabKey = panelKey as 'liveMarkets' | 'finalMarkets' | 'claimsMarkets';

    if (activeTabKey === 'claimsMarkets' && walletState.account && !claimsDataReady) {
      void ensureClaimsForLoadedRows();
    }
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activateTab(tab.dataset.tab || null));
  });

  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  const initialTab = !walletState.account && requestedTab === 'claimsMarkets' ? 'liveMarkets' : (requestedTab || 'liveMarkets');
  activateTab(initialTab);

  function updateRequiredCollateralLabel() {
    const liq = Number(liquidityInput.value || '0');
    const required = Number.isFinite(liq) && liq > 0 ? liq * 2 : 0;
    requiredCollateralEl.textContent = String(Math.floor(required));
  }

  async function refreshCreateBalances() {
    const liqRaw = liquidityInput.value.trim() || '0';
    const liquidity = parseUnits(liqRaw, 6);
    const requiredCollateral = liquidity * 2n;
    requiredCollateralCreateEl.textContent = `${fmtToken(requiredCollateral)} USDC`;

    if (!walletState.account) {
      walletUsdcCreateEl.textContent = '-';
      factoryAllowanceCreateEl.textContent = '-';
      createFundingStatusEl.textContent = 'Connect wallet';
      createFundingStatusEl.title = 'Connect wallet to view balance and allowance.';
      return;
    }

    try {
      const [walletUsdc, allowance] = await Promise.all([
        readClient.readContract({
          address: DEPLOYMENT.collateral as `0x${string}`,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [walletState.account as `0x${string}`],
        }),
        readClient.readContract({
          address: DEPLOYMENT.collateral as `0x${string}`,
          abi: erc20Abi,
          functionName: 'allowance',
          args: [walletState.account as `0x${string}`, DEPLOYMENT.factory as `0x${string}`],
        }),
      ]);

      const walletUsdcBn = walletUsdc as bigint;
      const allowanceBn = allowance as bigint;

      walletUsdcCreateEl.textContent = `${fmtToken(walletUsdcBn)} USDC`;
      factoryAllowanceCreateEl.textContent = allowanceBn >= requiredCollateral && requiredCollateral > 0n
        ? 'Ready'
        : `${fmtToken(allowanceBn)} USDC`;

      if (walletUsdcBn < requiredCollateral) {
        createFundingStatusEl.textContent = 'Insufficient balance';
        createFundingStatusEl.title = 'Wallet USDC is below required 2x initial liquidity collateral.';
      } else if (allowanceBn < requiredCollateral) {
        createFundingStatusEl.textContent = 'Approval needed';
        createFundingStatusEl.title = 'Factory allowance is below required collateral. Submit will request approval first.';
      } else {
        createFundingStatusEl.textContent = 'Ready';
        createFundingStatusEl.title = 'Funding checks pass for market creation.';
      }
    } catch {
      walletUsdcCreateEl.textContent = '-';
      factoryAllowanceCreateEl.textContent = '-';
      createFundingStatusEl.textContent = 'Status unavailable';
      createFundingStatusEl.title = 'Unable to fetch balance/allowance from chain.';
    }
  }

  liquidityInput.addEventListener('input', async () => {
    updateRequiredCollateralLabel();
    await refreshCreateBalances();
  });
  updateRequiredCollateralLabel();

  function setAccountLabel() {
    connectBtn.textContent = walletState.account ? shortAddr(walletState.account) : 'Connect Wallet';
    connectBtn.classList.toggle('connected', Boolean(walletState.account));
    const hasWallet = Boolean(walletState.account);
    claimsTabBtn.style.display = hasWallet ? '' : 'none';
    if (!hasWallet && activeTabKey === 'claimsMarkets') {
      activateTab('liveMarkets');
    }
  }

  function marketCardHref(address: string): string {
    return `/boxbet/?market=${address}`;
  }

  function inferCardStatus(row: EnrichedMarketRow): 'live' | 'final' | 'claims' {
    if (row.resolved) return row.claimable ? 'claims' : 'final';
    if (row.left <= 0 || row.tradingPaused) return 'final';
    return 'live';
  }

  function renderSearchMarketCard(row: EnrichedMarketRow): string {
    const status = inferCardStatus(row);
    const statusChipClass = status === 'live' ? 'live' : status === 'final' ? 'resolving' : '';
    const statusChipText = status === 'live' ? 'Live' : status === 'final' ? 'Final' : 'Claimable';
    const timingChipText = status === 'live' ? fmtDuration(Math.max(0, row.left)) : status === 'final' ? 'Now' : 'Ready';
    const claimLine = status === 'claims'
      ? `<div class="mono" style="font-size:.66rem; color:#80f0c0;">Estimated Payout ${fmtToken(row.claimPayout)} ${escapeHtml(row.collateralSymbol)}</div>`
      : '';
    const priorityChip = row.listed
      ? '<span class="meta-chip" style="border-color:#6ee7b7; color:#a7f3d0;">Listed</span>'
      : row.promoted > 0
        ? `<span class="meta-chip">Promoted ${row.promoted}</span>`
        : '';
    const yesIsLeader = row.yesReserve >= row.noReserve;
    const yesPercentClass = yesIsLeader ? 'market-share-green' : 'market-share-red';
    const noPercentClass = yesIsLeader ? 'market-share-red' : 'market-share-green';

    return `
      <a href="${marketCardHref(row.address)}" class="market-card market-search-hit ${status !== 'live' ? 'resolvable' : ''}">
        <div style="display:flex; justify-content:space-between; gap:8px; align-items:flex-start;">
          <div class="mono tiny">${shortAddr(row.address)}</div>
          <div class="mono" style="display:inline-flex; align-items:center; gap:6px; color:var(--muted); white-space:nowrap; font-size:.78rem;">
            <img src="${row.collateralLogoUrl}" alt="${escapeHtml(row.collateralSymbol)}" class="token-logo market-card-collateral-logo" style="width:18px; height:18px;"/>
            <span class="market-card-collateral-value">${fmtToken(row.collateralBalance, row.collateralDecimals)} ${escapeHtml(row.collateralSymbol)}</span>
          </div>
        </div>
        <div class="q">${escapeHtml(row.question)}</div>
        <div class="chips">
          <span class="meta-chip ${statusChipClass}">${statusChipText}</span>
          <span class="meta-chip">${timingChipText}</span>
          ${priorityChip}
        </div>
        <div style="display:grid; gap:5px;">
          <div class="mono" style="font-size:.7rem; color:var(--muted); display:flex; flex-wrap:wrap; gap:6px; align-items:center;">
            <span class="market-token-pill">${escapeHtml(row.yesLabel)}</span>
            <span class="${yesPercentClass}">${fmtPct(row.yesReserve, row.total)}</span>
            <span>/</span>
            <span class="market-token-pill">${escapeHtml(row.noLabel)}</span>
            <span class="${noPercentClass}">${fmtPct(row.noReserve, row.total)}</span>
          </div>
          ${claimLine}
        </div>
      </a>
    `;
  }

  async function resolveMarketByAddress(address: `0x${string}`): Promise<EnrichedMarketRow> {
    const known = loadedRows.find((row) => row.address.toLowerCase() === address.toLowerCase());
    if (known) return known;

    const now = Math.floor(Date.now() / 1000);
    const [resolveAfterRaw, resolvedRaw, yesReserveRaw, noReserveRaw] = await readContractsBatch<unknown>([
      { address, abi: marketAbi, functionName: 'resolveAfter' },
      { address, abi: marketAbi, functionName: 'resolved' },
      { address, abi: marketAbi, functionName: 'yesReserve' },
      { address, abi: marketAbi, functionName: 'noReserve' },
    ]);

    const resolveAfter = Number(resolveAfterRaw ?? 0n);
    const yesReserve = BigInt((yesReserveRaw as bigint) ?? 0n);
    const noReserve = BigInt((noReserveRaw as bigint) ?? 0n);
    const total = yesReserve + noReserve;

    const baseRows = [{
      address,
      question: 'Unknown market',
      resolveAfter,
      resolved: Boolean(resolvedRaw),
      tradingPaused: false,
      yesReserve,
      noReserve,
      yesTokenAddr: '0x0000000000000000000000000000000000000000' as `0x${string}`,
      noTokenAddr: '0x0000000000000000000000000000000000000000' as `0x${string}`,
      marketValue: 0n,
      total,
      left: resolveAfter - now,
    }];

    const [enriched] = await enrichBaseRows(baseRows, Boolean(walletState.account));
    if (!enriched) throw new Error('Market not found');
    return enriched;
  }

  function setSearchModalOpen(open: boolean): void {
    if (open) {
      marketSearchModal.hidden = false;
      window.requestAnimationFrame(() => {
        marketSearchModal.classList.add('is-open');
      });
      marketSearchInput.focus();
      return;
    }
    marketSearchModal.classList.remove('is-open');
    marketSearchModal.hidden = true;
  }

  async function runMarketSearch(): Promise<void> {
    const raw = marketSearchInput.value.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(raw)) {
      marketSearchStatus.textContent = 'Enter a valid 42-character contract address.';
      marketSearchResult.innerHTML = '';
      return;
    }

    const normalized = raw as `0x${string}`;
    marketSearchSubmitBtn.disabled = true;
    marketSearchStatus.textContent = 'Searching market...';
    marketSearchResult.innerHTML = '';

    try {
      const row = await resolveMarketByAddress(normalized);
      marketSearchResult.innerHTML = renderSearchMarketCard(row);
      marketSearchStatus.textContent = `Found market ${shortAddr(row.address)}.`;
    } catch {
      marketSearchStatus.textContent = 'No market found at this address on the selected network.';
      marketSearchResult.innerHTML = '';
    } finally {
      marketSearchSubmitBtn.disabled = false;
    }
  }

  async function runTxConfirmationModal(config: {
    title: string;
    bodyLines: string[];
    submitLabel: string;
    submitButtonClass?: string;
    submitAction: () => Promise<void>;
    approval?: {
      label: string;
      checkNeeded: () => Promise<boolean>;
      approveAction: () => Promise<void>;
    };
  }): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const overlay = document.createElement('div');
      overlay.className = 'confirm-modal-overlay';
      overlay.innerHTML = `
        <div class="confirm-modal-card card" role="dialog" aria-modal="true" aria-label="${config.title}">
          <div class="confirm-modal-title">${config.title}</div>
          <div class="confirm-modal-body" style="display:grid; gap:10px;">
            <div style="display:grid; gap:6px;">${config.bodyLines.map((line) => `<div>${line}</div>`).join('')}</div>
            <div id="txFlowStatus" class="tx-flow-status">Checking requirements...</div>
          </div>
          <div class="confirm-modal-actions">
            <button type="button" class="btn" data-action="cancel">Cancel</button>
            ${config.approval ? `<button type="button" class="btn btn-amber" data-action="approve">${config.approval.label}</button>` : ''}
            <button type="button" class="btn ${config.submitButtonClass || 'btn-primary'}" data-action="submit">${config.submitLabel}</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);

      const statusEl = overlay.querySelector('#txFlowStatus') as HTMLDivElement;
      const cancelBtn = overlay.querySelector('[data-action="cancel"]') as HTMLButtonElement;
      const approveBtn = overlay.querySelector('[data-action="approve"]') as HTMLButtonElement | null;
      const submitBtn = overlay.querySelector('[data-action="submit"]') as HTMLButtonElement;

      let approvalStillNeeded = false;

      const cleanup = () => {
        overlay.remove();
      };

      const setBusy = (button: HTMLButtonElement, busy: boolean, idleLabel: string) => {
        button.disabled = busy;
        button.innerHTML = busy
          ? `<span class="tx-btn-spinner" aria-hidden="true"></span>${idleLabel}`
          : idleLabel;
      };

      const fail = (error: any) => {
        cleanup();
        reject(error);
      };

      const refreshApprovalState = async () => {
        if (!config.approval) {
          submitBtn.disabled = false;
          statusEl.textContent = 'Ready to submit.';
          return;
        }

        approvalStillNeeded = await config.approval.checkNeeded();
        if (approvalStillNeeded) {
          submitBtn.disabled = true;
          statusEl.textContent = 'Approval required before submit.';
          if (approveBtn) {
            approveBtn.style.display = '';
            approveBtn.disabled = false;
          }
          return;
        }

        submitBtn.disabled = false;
        statusEl.textContent = 'Approval satisfied. Ready to submit.';
        if (approveBtn) {
          approveBtn.style.display = 'none';
        }
      };

      cancelBtn.addEventListener('click', () => {
        cleanup();
        reject(new Error('Transaction cancelled.'));
      });

      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          cleanup();
          reject(new Error('Transaction cancelled.'));
        }
      });

      approveBtn?.addEventListener('click', async () => {
        if (!config.approval) return;
        try {
          setBusy(approveBtn, true, config.approval.label);
          submitBtn.disabled = true;
          statusEl.textContent = 'Sending approval transaction...';
          await config.approval.approveAction();
          setBusy(approveBtn, false, config.approval.label);
          await refreshApprovalState();
        } catch (error: any) {
          setBusy(approveBtn, false, config.approval.label);
          fail(error);
        }
      });

      submitBtn.addEventListener('click', async () => {
        try {
          if (config.approval && approvalStillNeeded) {
            statusEl.textContent = 'Approval must be completed first.';
            return;
          }
          setBusy(submitBtn, true, config.submitLabel);
          cancelBtn.disabled = true;
          if (approveBtn) approveBtn.disabled = true;
          statusEl.textContent = 'Submitting transaction...';
          await config.submitAction();
          cleanup();
          resolve();
        } catch (error: any) {
          fail(error);
        }
      });

      refreshApprovalState().catch(fail);
    });
  }

  function updateLoadMoreVisibility() {
    if (pageLoading) {
      loadMoreBtn.style.display = 'none';
      loadMoreBtn.disabled = true;
      return;
    }

    const byCursor = Math.max(0, nextIndexCursor + 1);
    const byCap = Math.max(0, MAX_MARKETS_VISIBLE - loadedRows.length);
    const remaining = Math.min(byCursor, byCap);
    if (remaining > 0) {
      loadMoreBtn.style.display = '';
      loadMoreBtn.disabled = pageLoading;
      loadMoreBtn.textContent = `Load More Markets (${remaining} remaining)`;
      return;
    }
    loadMoreBtn.style.display = 'none';
    loadMoreBtn.disabled = true;
  }

  async function enrichBaseRows(
    baseRows: Array<{
      address: `0x${string}`;
      question: string;
      resolveAfter: number;
      resolved: boolean;
      tradingPaused: boolean;
      yesReserve: bigint;
      noReserve: bigint;
      yesTokenAddr: `0x${string}`;
      noTokenAddr: `0x${string}`;
      marketValue: bigint;
      total: bigint;
      left: number;
    }>,
    includeClaimability: boolean,
  ): Promise<EnrichedMarketRow[]> {
    const claimabilityByAddress = new Map<`0x${string}`, { canClaim: boolean; payout: bigint }>();
    if (includeClaimability && walletState.account) {
      const settledRows = baseRows.filter((row) => row.resolved);
      if (settledRows.length > 0) {
        const quoteCalls = settledRows.map((row) => ({
          address: row.address,
          abi: marketAbi,
          functionName: 'quoteClaim',
          args: [walletState.account as `0x${string}`],
        }));
        const quoteResults = await readContractsBatch<unknown>(quoteCalls);

        settledRows.forEach((row, idx) => {
          const quote = quoteResults[idx] as readonly unknown[] | null;
          claimabilityByAddress.set(row.address, {
            canClaim: Boolean(quote?.[0]),
            payout: typeof quote?.[1] === 'bigint' ? (quote[1] as bigint) : 0n,
          });
        });
      }
    }

    const staticDetails = await resolveMarketStaticDetails(baseRows.map((row) => row.address));

    const rowCollateralByAddress = new Map<string, `0x${string}`>();
    const uniqueCollaterals = new Set<`0x${string}`>();
    for (const row of baseRows) {
      const staticDetail = staticDetails.get(row.address.toLowerCase());
      const collateralAddr = staticDetail?.collateralAddr || (DEPLOYMENT.collateral as `0x${string}`);
      rowCollateralByAddress.set(row.address.toLowerCase(), collateralAddr);
      uniqueCollaterals.add(collateralAddr);
    }

    const collateralMetaByAddress = new Map<string, { symbol: string; decimals: number; logoUrl: string }>();
    const collateralList = Array.from(uniqueCollaterals);
    if (collateralList.length > 0) {
      const collateralMetaCalls = collateralList.flatMap((address) => [
        { address, abi: erc20Abi, functionName: 'symbol' },
        { address, abi: erc20Abi, functionName: 'decimals' },
      ]);
      const collateralMetaReads = await readContractsBatch<unknown>(collateralMetaCalls);
      for (let idx = 0; idx < collateralList.length; idx += 1) {
        const address = collateralList[idx];
        const offset = idx * 2;
        const rawSymbol = String(collateralMetaReads[offset] ?? 'TOKEN').trim();
        const symbol = rawSymbol || 'TOKEN';
        const rawDecimals = Number(collateralMetaReads[offset + 1] ?? 18);
        const decimals = Number.isFinite(rawDecimals) && rawDecimals >= 0 && rawDecimals <= 36 ? rawDecimals : 18;
        const logoUrl = resolveTokenLogoUrlSync(address) || `${import.meta.env.BASE_URL}images/coin.svg`;
        collateralMetaByAddress.set(address.toLowerCase(), { symbol, decimals, logoUrl });
      }
    }

    const collateralBalanceCalls = baseRows.map((row) => {
      const collateralAddr = rowCollateralByAddress.get(row.address.toLowerCase()) || (DEPLOYMENT.collateral as `0x${string}`);
      return {
        address: collateralAddr,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [row.address],
      };
    });
    const collateralBalanceReads = await readContractsBatch<unknown>(collateralBalanceCalls);

    return baseRows.map((row, idx) => {
      const staticDetail = staticDetails.get(row.address.toLowerCase());
      const claimInfo = claimabilityByAddress.get(row.address) || { canClaim: false, payout: 0n };
      const priorityInfo = marketPriorityMap.get(row.address.toLowerCase()) || { listed: false, promoted: 0 };
      const collateralAddr = rowCollateralByAddress.get(row.address.toLowerCase()) || (DEPLOYMENT.collateral as `0x${string}`);
      const collateralMeta = collateralMetaByAddress.get(collateralAddr.toLowerCase()) || {
        symbol: 'TOKEN',
        decimals: 18,
        logoUrl: `${import.meta.env.BASE_URL}images/coin.svg`,
      };
      const collateralBalanceRaw = collateralBalanceReads[idx];
      const collateralBalance = typeof collateralBalanceRaw === 'bigint' ? collateralBalanceRaw : 0n;
      return {
        ...row,
        question: staticDetail?.question || row.question,
        collateralAddr,
        yesTokenAddr: staticDetail?.yesTokenAddr || row.yesTokenAddr,
        noTokenAddr: staticDetail?.noTokenAddr || row.noTokenAddr,
        yesLabel: staticDetail?.yesLabel || '-',
        noLabel: staticDetail?.noLabel || '-',
        collateralSymbol: collateralMeta.symbol,
        collateralDecimals: collateralMeta.decimals,
        collateralLogoUrl: collateralMeta.logoUrl,
        collateralBalance,
        claimable: includeClaimability ? claimInfo.canClaim : false,
        claimPayout: includeClaimability ? claimInfo.payout : 0n,
        listed: priorityInfo.listed,
        promoted: priorityInfo.promoted,
        priorityKey: getMarketPrioritySortKey(priorityInfo),
      };
    });
  }

  async function loadPageViaLooker(
    now: number,
    start: number,
    end: number,
    includeClaimability: boolean,
  ): Promise<EnrichedMarketRow[]> {
    const pageCount = start - end + 1;
    const snapshots = (await readClient.readContract({
      address: DEPLOYMENT.looker as `0x${string}`,
      abi: lookerAbi,
      functionName: 'getMarketSnapshots',
      args: [DEPLOYMENT.factory as `0x${string}`, BigInt(end), BigInt(pageCount)],
    })) as Array<{
      market: Address;
      resolveAfter: bigint;
      resolved: boolean;
      yesReserve: bigint;
      noReserve: bigint;
    }>;

    if (!Array.isArray(snapshots) || snapshots.length === 0) return [];

    const reversed = [...snapshots].reverse();
    const addresses = reversed.map((snapshot) => String(snapshot.market) as `0x${string}`);

    const baseRows = reversed.map((snapshot, idx) => {
      const yesReserve = BigInt(snapshot.yesReserve || 0n);
      const noReserve = BigInt(snapshot.noReserve || 0n);
      const total = yesReserve + noReserve;
      const resolveAfter = Number(snapshot.resolveAfter || 0n);
      return {
        address: addresses[idx],
        question: 'Unknown market',
        resolveAfter,
        resolved: Boolean(snapshot.resolved),
        tradingPaused: false,
        yesReserve,
        noReserve,
        yesTokenAddr: '0x0000000000000000000000000000000000000000' as `0x${string}`,
        noTokenAddr: '0x0000000000000000000000000000000000000000' as `0x${string}`,
        marketValue: 0n,
        total,
        left: resolveAfter - now,
      };
    });

    return enrichBaseRows(baseRows, includeClaimability);
  }

  async function loadPageViaLegacyFactory(
    now: number,
    start: number,
    end: number,
    includeClaimability: boolean,
  ): Promise<EnrichedMarketRow[]> {
    const indices: bigint[] = [];
    for (let i = start; i >= end; i -= 1) indices.push(BigInt(i));

    const addressCalls = indices.map((i) => ({
      address: DEPLOYMENT.factory as `0x${string}`,
      abi: factoryAbi,
      functionName: 'markets',
      args: [i],
    }));
    const addresses = (await readContractsBatch<`0x${string}` | null>(addressCalls))
      .filter((value): value is `0x${string}` => Boolean(value));

    if (addresses.length === 0) return [];

    const detailCalls = addresses.flatMap((address) => [
      { address, abi: marketAbi, functionName: 'resolveAfter' },
      { address, abi: marketAbi, functionName: 'resolved' },
      { address, abi: marketAbi, functionName: 'yesReserve' },
      { address, abi: marketAbi, functionName: 'noReserve' },
    ]);
    const detail = await readContractsBatch<unknown>(detailCalls);

    const baseRows = addresses.map((address, idx) => {
      const offset = idx * 4;
      const resolveAfter = Number(detail[offset] ?? 0n);
      const resolved = Boolean(detail[offset + 1]);
      const yesReserve = BigInt((detail[offset + 2] as bigint) ?? 0n);
      const noReserve = BigInt((detail[offset + 3] as bigint) ?? 0n);
      const total = yesReserve + noReserve;
      const left = resolveAfter - now;
      return {
        address,
        question: 'Unknown market',
        resolveAfter,
        resolved,
        tradingPaused: false,
        yesReserve,
        noReserve,
        yesTokenAddr: '0x0000000000000000000000000000000000000000' as `0x${string}`,
        noTokenAddr: '0x0000000000000000000000000000000000000000' as `0x${string}`,
        marketValue: 0n,
        total,
        left,
      };
    });

    return enrichBaseRows(baseRows, includeClaimability);
  }

  async function ensureClaimsForLoadedRows(): Promise<void> {
    if (!walletState.account || loadedRows.length === 0) {
      claimsDataReady = false;
      renderMarketSections();
      return;
    }

    const settledRows = loadedRows.filter((row) => row.resolved);
    if (settledRows.length === 0) {
      claimsDataReady = true;
      renderMarketSections();
      return;
    }

    const quoteCalls = settledRows.map((row) => ({
      address: row.address,
      abi: marketAbi,
      functionName: 'quoteClaim',
      args: [walletState.account as `0x${string}`],
    }));
    const quoteResults = await readContractsBatch<unknown>(quoteCalls);
    const claimMap = new Map<`0x${string}`, { canClaim: boolean; payout: bigint }>();

    settledRows.forEach((row, idx) => {
      const quote = quoteResults[idx] as readonly unknown[] | null;
      claimMap.set(row.address, {
        canClaim: Boolean(quote?.[0]),
        payout: typeof quote?.[1] === 'bigint' ? (quote[1] as bigint) : 0n,
      });
    });

    loadedRows = loadedRows.map((row) => {
      if (!row.resolved) return row;
      const claimInfo = claimMap.get(row.address) || { canClaim: false, payout: 0n };
      return {
        ...row,
        claimable: claimInfo.canClaim,
        claimPayout: claimInfo.payout,
      };
    });
    claimsDataReady = true;
    renderMarketSections();
  }

  async function refreshMarketListAuto(): Promise<void> {
    if (loadedRows.length === 0 || nextIndexCursor < 0 || pageLoading || loadMarketsInFlight) {
      await loadMarkets();
      return;
    }

    const latestCount = await getMarketCount();
    if (latestCount <= totalMarketCount) {
      return;
    }

    const newCount = latestCount - totalMarketCount;
    if (newCount >= AUTO_REFRESH_RETENTION_LIMIT) {
      await loadMarkets();
      return;
    }

    const fetchCount = Math.min(newCount, AUTO_REFRESH_NEW_HEAD_LIMIT, latestCount);
    const start = latestCount - 1;
    const end = Math.max(0, start - fetchCount + 1);
    const now = Math.floor(Date.now() / 1000);
    const includeClaimability = shouldLoadClaimsData();

    let headRows: EnrichedMarketRow[] = [];
    try {
      headRows = await loadPageViaLooker(now, start, end, includeClaimability);
    } catch {
      headRows = await loadPageViaLegacyFactory(now, start, end, includeClaimability);
    }

    const byAddress = new Map<string, EnrichedMarketRow>();
    for (const row of headRows) byAddress.set(row.address.toLowerCase(), row);
    for (const row of loadedRows) {
      const key = row.address.toLowerCase();
      if (!byAddress.has(key)) byAddress.set(key, row);
    }

    loadedRows = Array.from(byAddress.values()).slice(0, MAX_MARKETS_VISIBLE);
    totalMarketCount = latestCount;
    nextIndexCursor = Math.max(-1, latestCount - loadedRows.length - 1);

    if (!includeClaimability) {
      claimsDataReady = false;
    }

    renderMarketSections();
  }

  function renderMarketSections() {
    const renderEmpty = (message: string): string => `<div class="mono" style="color:var(--muted)">${message}</div>`;
    const comparePriority = (a: EnrichedMarketRow, b: EnrichedMarketRow): number => b.priorityKey - a.priorityKey;

    const liveRows = loadedRows
      .filter((row) => !row.resolved && row.left > 0 && !row.tradingPaused)
      .sort((a, b) => comparePriority(a, b) || a.resolveAfter - b.resolveAfter);
    const finalRows = loadedRows
      .filter((row) => !row.resolved && (row.left <= 0 || row.tradingPaused))
      .sort((a, b) => comparePriority(a, b) || a.resolveAfter - b.resolveAfter);
    const claimsRows = walletState.account
      ? loadedRows
        .filter((row) => row.resolved && row.claimable)
        .sort((a, b) => comparePriority(a, b) || (a.claimPayout === b.claimPayout ? b.resolveAfter - a.resolveAfter : (a.claimPayout > b.claimPayout ? -1 : 1)))
      : [];

    liveCountEl.textContent = `(${liveRows.length})`;
    finalCountEl.textContent = `(${finalRows.length})`;
    claimsCountEl.textContent = walletState.account
      ? (claimsDataReady ? `(${claimsRows.length})` : '(...)')
      : '(0)';

    const renderMarketCard = (row: EnrichedMarketRow, status: 'live' | 'final' | 'claims'): string => {
      const statusChipClass = status === 'live' ? 'live' : status === 'final' ? 'resolving' : '';
      const statusChipText = status === 'live' ? 'Live' : status === 'final' ? 'Final' : 'Claimable';
      const timingChipText = status === 'live' ? fmtDuration(row.left) : status === 'final' ? 'Now' : 'Ready';
      const claimLine = status === 'claims'
        ? `<div class="mono" style="font-size:.66rem; color:#80f0c0;">Estimated Payout ${fmtToken(row.claimPayout)} USDC</div>`
        : '';
      const priorityChip = row.listed
        ? '<span class="meta-chip" style="border-color:#6ee7b7; color:#a7f3d0;">Listed</span>'
        : row.promoted > 0
          ? `<span class="meta-chip">Promoted ${row.promoted}</span>`
          : '';
      const yesIsLeader = row.yesReserve >= row.noReserve;
      const yesPercentClass = yesIsLeader ? 'market-share-green' : 'market-share-red';
      const noPercentClass = yesIsLeader ? 'market-share-red' : 'market-share-green';

      return `
        <a href="?market=${row.address}" class="market-card ${status !== 'live' ? 'resolvable' : ''}">
          <div style="display:flex; justify-content:space-between; gap:8px; align-items:flex-start;">
            <div class="mono tiny">${shortAddr(row.address)}</div>
            <div class="mono" style="display:inline-flex; align-items:center; gap:6px; color:var(--muted); white-space:nowrap; font-size:.78rem;">
              <img src="${row.collateralLogoUrl}" alt="${escapeHtml(row.collateralSymbol)}" class="token-logo market-card-collateral-logo" style="width:18px; height:18px;"/>
              <span class="market-card-collateral-value">${fmtToken(row.collateralBalance, row.collateralDecimals)} ${escapeHtml(row.collateralSymbol)}</span>
            </div>
          </div>
          <div class="q">${escapeHtml(row.question)}</div>
          <div class="chips">
            <span class="meta-chip ${statusChipClass}">${statusChipText}</span>
            <span class="meta-chip">${timingChipText}</span>
            ${priorityChip}
          </div>
          <div style="display:grid; gap:5px;">
            <div class="mono" style="font-size:.7rem; color:var(--muted); display:flex; flex-wrap:wrap; gap:6px; align-items:center;">
              <span class="market-token-pill">${escapeHtml(row.yesLabel)}</span>
              <span class="${yesPercentClass}">${fmtPct(row.yesReserve, row.total)}</span>
              <span>/</span>
              <span class="market-token-pill">${escapeHtml(row.noLabel)}</span>
              <span class="${noPercentClass}">${fmtPct(row.noReserve, row.total)}</span>
            </div>
            ${claimLine}
          </div>
        </a>
      `;
    };

    liveMarketsEl.innerHTML = liveRows.length
      ? liveRows.map((row) => renderMarketCard(row, 'live')).join('')
      : renderEmpty('No live markets right now.');

    finalMarketsEl.innerHTML = finalRows.length
      ? finalRows.map((row) => renderMarketCard(row, 'final')).join('')
      : renderEmpty('No finalizable markets right now.');

    if (!walletState.account) {
      claimsHintEl.textContent = 'Connect wallet to load claimable markets for your address.';
      claimsMarketsEl.innerHTML = renderEmpty('Connect wallet to view claimable markets.');
    } else {
      claimsHintEl.textContent = !claimsDataReady
        ? 'Open the Claims tab to load claimable quotes on demand.'
        : claimsRows.length > 0
          ? 'Claimable markets are finalized and currently have a non-zero claim quote for this wallet.'
          : 'No finalized markets are currently claimable for this wallet.';
      claimsMarketsEl.innerHTML = claimsRows.length
        ? claimsRows.map((row) => renderMarketCard(row, 'claims')).join('')
        : renderEmpty(claimsDataReady ? 'No claimable markets yet.' : 'Open this tab to load claimable markets.');
    }

    persistMarketListCache();
    updateLoadMoreVisibility();
  }

  async function loadNextMarketPage(): Promise<void> {
    if (pageLoading || nextIndexCursor < 0) {
      updateLoadMoreVisibility();
      return;
    }

    const currentGeneration = loadingGeneration;
    setMarketsLoading(true);
    updateLoadMoreVisibility();

    try {
      const now = Math.floor(Date.now() / 1000);
      const start = nextIndexCursor;
      const remainingCap = Math.max(0, MAX_MARKETS_VISIBLE - loadedRows.length);
      if (remainingCap <= 0) {
        nextIndexCursor = -1;
        renderMarketSections();
        return;
      }
      const requestedPageSize = Math.max(1, Math.min(dynamicPageSize, start + 1, remainingCap));
      const end = Math.max(0, start - requestedPageSize + 1);
      nextIndexCursor = end - 1;

      let pageRows: EnrichedMarketRow[] = [];
      const reducedPageSize = Math.max(1, Math.min(MARKET_PAGE_SIZE_REDUCED, start + 1));
      const includeClaimability = shouldLoadClaimsData();

      try {
        pageRows = await loadPageViaLooker(now, start, end, includeClaimability);
      } catch {
        dynamicPageSize = MARKET_PAGE_SIZE_REDUCED;
        const reducedEnd = Math.max(0, start - reducedPageSize + 1);
        try {
          pageRows = await loadPageViaLooker(now, start, reducedEnd, includeClaimability);
          nextIndexCursor = reducedEnd - 1;
        } catch {
          try {
            pageRows = await loadPageViaLegacyFactory(now, start, reducedEnd, includeClaimability);
            nextIndexCursor = reducedEnd - 1;
          } catch (legacyError) {
            nextIndexCursor = start;
            throw legacyError;
          }
        }
      }

      if (pageRows.length === 0) {
        renderMarketSections();
        return;
      }

      if (currentGeneration !== loadingGeneration) return;
      loadedRows = dedupeRowsByAddress(loadedRows.concat(pageRows));
      renderMarketSections();
    } finally {
      setMarketsLoading(false);
      updateLoadMoreVisibility();
    }
  }

  async function loadMarkets() {
    if (loadMarketsInFlight) return loadMarketsInFlight;

    const run = (async () => {
      const previousRows = loadedRows;
      loadingGeneration += 1;
      const currentGeneration = loadingGeneration;
      setMarketsLoading(false);
      nextIndexCursor = -1;
      dynamicPageSize = MARKET_PAGE_SIZE;

      if (previousRows.length === 0) {
        claimsHintEl.textContent = '';
        loadMoreBtn.style.display = 'none';
      }

      let count = 0;
      try {
        count = await getMarketCount();
      } catch (error) {
        loadedRows = previousRows;
        renderMarketSections();
        throw error;
      }
      if (currentGeneration !== loadingGeneration) return;

      totalMarketCount = count;

      if (count === 0) {
        liveCountEl.textContent = '(0)';
        finalCountEl.textContent = '(0)';
        claimsCountEl.textContent = '(0)';
        liveMarketsEl.innerHTML = '<div class="mono" style="color:var(--muted)">No markets yet.</div>';
        finalMarketsEl.innerHTML = '<div class="mono" style="color:var(--muted)">No markets awaiting finalization.</div>';
        claimsHintEl.textContent = walletState.account
          ? 'No claimable markets for this wallet yet.'
          : 'Connect wallet to evaluate claimable markets.';
        claimsMarketsEl.innerHTML = `<div class="mono" style="color:var(--muted)">${walletState.account ? 'No claimable markets yet.' : 'Connect wallet to load claimable markets.'}</div>`;
        return;
      }

      nextIndexCursor = count - 1;
      try {
        await loadNextMarketPage();
      } catch (error) {
        loadedRows = previousRows;
        renderMarketSections();
        throw error;
      }
    })();

    loadMarketsInFlight = run.finally(() => {
      if (loadMarketsInFlight === run) {
        loadMarketsInFlight = null;
      }
    });

    return loadMarketsInFlight;
  }

  loadMoreBtn.addEventListener('click', async () => {
    await loadNextMarketPage();
  });

  connectBtn.addEventListener('click', async () => {
    if (walletState.account) {
      await openAccountModal({
        onWalletStateChange: async () => {
          setAccountLabel();
          try {
            await loadMarkets();
          } catch (error: any) {
            showToast('warning', error?.message || 'Using last known market data due to RPC failure');
          }
          if (modal.style.display === 'flex') await refreshCreateBalances();
          scheduleListAutoRefresh();
        },
      });
      return;
    }

    try {
      await connectWallet();
      const chainOk = await ensurePulseChainConnected();
      setAccountLabel();
      if (!chainOk) {
        showToast('warning', `Switch to ${getActiveChainConfig().chainName} to continue`);
        return;
      }
      showToast('success', `Wallet connected: ${walletState.account}`);
      try {
        await loadMarkets();
      } catch (error: any) {
        showToast('warning', error?.message || 'Using last known market data due to RPC failure');
      }
      if (modal.style.display === 'flex') await refreshCreateBalances();
      scheduleListAutoRefresh();
    } catch (error: any) {
      showToast('error', error?.message || 'Wallet connect failed');
      scheduleListAutoRefresh();
    }
  });

  refreshBtn.addEventListener('click', async () => {
    try {
      await loadMarkets();
      showToast('success', 'Market list refreshed');
    } catch (error: any) {
      showToast('warning', error?.message || 'Using last known market data due to RPC failure');
    } finally {
      scheduleListAutoRefresh();
    }
  });

  headerCreateMarketBtn.addEventListener('click', async () => {
    linksDropdown.open = false;
    window.location.href = `${import.meta.env.BASE_URL}create/`;
  });

  (app.querySelector('#cancelCreate') as HTMLButtonElement).addEventListener('click', () => {
    modal.style.display = 'none';
  });

  (app.querySelector('#confirmCreate') as HTMLButtonElement).addEventListener('click', async () => {
    const q = (app.querySelector('#q') as HTMLInputElement).value.trim();
    const yesName = (app.querySelector('#yesName') as HTMLInputElement).value.trim();
    const yesSymbol = (app.querySelector('#yesSymbol') as HTMLInputElement).value.trim();
    const noName = (app.querySelector('#noName') as HTMLInputElement).value.trim();
    const noSymbol = (app.querySelector('#noSymbol') as HTMLInputElement).value.trim();
    const h = Number((app.querySelector('#h') as HTMLInputElement).value || '24');
    const liqRaw = (app.querySelector('#liq') as HTMLInputElement).value.trim();
    const encoder = new TextEncoder();
    const invalidLabel = [yesName, yesSymbol, noName, noSymbol].some((value) => {
      const len = encoder.encode(value).length;
      return len === 0 || len > MAX_OUTCOME_TOKEN_LABEL_BYTES;
    });

    if (!q) {
      showToast('warning', 'Question is required');
      return;
    }
    if (invalidLabel) {
      showToast('warning', 'Token names/symbols must be 1 to 25 UTF-8 bytes');
      return;
    }
    if (!liqRaw || Number(liqRaw) <= 0) {
      showToast('warning', 'Initial liquidity must be greater than zero');
      return;
    }

    try {
      const liquidity = parseUnits(liqRaw, 6);
      const requiredCollateral = liquidity * 2n;
      const resolveAfter = Math.floor(Date.now() / 1000) + Math.max(1, h) * 3600;

      await runTxConfirmationModal({
        title: 'Create Market',
        bodyLines: [
          `Question: ${q}`,
          `Outcome 1: ${yesName} (${yesSymbol})`,
          `Outcome 2: ${noName} (${noSymbol})`,
          `Resolve in: ${Math.max(1, h)} hours`,
          `Initial Liquidity: ${liqRaw} USDC`,
          `Required Collateral: ${fmtToken(requiredCollateral)} USDC`,
        ],
        submitLabel: 'Create Market',
        submitButtonClass: 'btn-primary',
        approval: {
          label: 'Approve USDC',
          checkNeeded: async () => {
            if (!walletState.account) throw new Error('Connect wallet first');
            const allowance = await readClient.readContract({
              address: DEPLOYMENT.collateral as `0x${string}`,
              abi: erc20Abi,
              functionName: 'allowance',
              args: [walletState.account as `0x${string}`, DEPLOYMENT.factory as `0x${string}`],
            });
            return (allowance as bigint) < requiredCollateral;
          },
          approveAction: async () => {
            const token = contractWithSigner(DEPLOYMENT.collateral, erc20AbiEthers);
            const approveTx = await token.approve(DEPLOYMENT.factory, requiredCollateral);
            showToast('warning', 'Approval transaction submitted', { txHash: approveTx.hash, linkLabel: 'Open approval tx' });
            await approveTx.wait();
          },
        },
        submitAction: async () => {
          const factory = contractWithSigner(DEPLOYMENT.factory, factoryAbiEthers);
          const tx = await factory.createMarket(q, resolveAfter, liquidity, yesName, yesSymbol, noName, noSymbol);
          showToast('success', 'Create market transaction submitted', { txHash: tx.hash, linkLabel: 'Open create tx' });
          await tx.wait();
          modal.style.display = 'none';
          showToast('success', 'Market created');
          schedulePostMutationListRefreshes();
          await refreshCreateBalances();
        },
      });
    } catch (error: any) {
      showToast('error', error?.shortMessage || error?.message || 'Create failed');
    }
  });

  bindWalletEvents(async () => {
    setAccountLabel();
    if (walletState.account) {
      await ensurePulseChainConnected();
    }
    claimsDataReady = false;
    try {
      await loadMarkets();
    } catch (error: any) {
      showToast('warning', error?.message || 'Using last known market data due to RPC failure');
    }
    if (modal.style.display === 'flex') await refreshCreateBalances();
    scheduleListAutoRefresh();
  });

  void rehydrateWalletSession().then(() => {
    setAccountLabel();
    if (walletState.account) {
      void ensurePulseChainConnected();
      void loadMarkets();
      scheduleListAutoRefresh();
    }
    if (modal.style.display === 'flex') {
      void refreshCreateBalances();
    }
  }).catch(() => {
    // no-op
  });

  document.addEventListener('visibilitychange', onPageVisibilityOrFocusChanged);
  marketSearchBtn.addEventListener('click', () => {
    marketSearchStatus.textContent = 'Enter an address to begin.';
    marketSearchResult.innerHTML = '';
    setSearchModalOpen(true);
  });
  marketSearchCloseBtn.addEventListener('click', () => {
    setSearchModalOpen(false);
  });
  marketSearchModal.addEventListener('click', (event) => {
    if (event.target === marketSearchModal) {
      setSearchModalOpen(false);
    }
  });
  marketSearchInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void runMarketSearch();
    }
  });
  marketSearchSubmitBtn.addEventListener('click', () => {
    void runMarketSearch();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !marketSearchModal.hidden) {
      setSearchModalOpen(false);
    }
  });
  window.addEventListener('focus', onPageVisibilityOrFocusChanged);
  window.addEventListener('blur', onPageVisibilityOrFocusChanged);
  window.addEventListener('beforeunload', clearListRefreshTimers);

  setAccountLabel();
  restoreMarketListCache();
  loadMarkets()
    .then(() => {
      scheduleListAutoRefresh();
    })
    .catch((error) => showToast('warning', error?.message || 'Using last known market data due to RPC failure'));
}
